import { match } from 'ts-pattern';

import { cleanContentfulId } from '@telialt/utils/src/Contentful/cleanContentfulId';

import { BlockContentType, CardType } from '../../models/Content';
import { transformCta, transformResponsiveImage } from '../../utils/transformers';
import {
    BlockContentBackground,
    BlockContentCardBackground,
    BlockContentData,
    ContentCardAlignment,
    ContentCardData,
} from './model';

export const transformContentCard = (
    card: CardType,
    blockBackgroundColor: BlockContentBackground,
    index: number,
): ContentCardData => {
    const cardColorOption = cleanContentfulId(card.fields.colorOption);

    const alignment = match<CardType['fields']['displayOption'], ContentCardAlignment>(
        cleanContentfulId(card.fields.displayOption),
    )
        .with('alignment-left', () => 'left')
        .with('alignment-right', () => 'right')
        .otherwise(() => undefined);

    const backgroundColor = match<CardType['fields']['colorOption'], BlockContentCardBackground>(cardColorOption)
        .with('color-dark-purple', () => 'dark')
        .otherwise(() => undefined);

    const isNegativeFromCard = cardColorOption === 'color-dark-purple';
    const isNegativeFromBlock = blockBackgroundColor === 'dark';
    const video = card.fields.video;

    return {
        id: `${card.sys.id}-${index}`,
        alignment,
        backgroundColor,
        isNegative: isNegativeFromCard || isNegativeFromBlock,
        title: card.fields.title,
        badge: card.fields.badge,
        badgeIcon: cleanContentfulId(card.fields.badgeIcon),
        description: card.fields.description,
        descriptionList: card.fields.list,
        media: transformResponsiveImage(card.fields.media),
        ctas: card.fields.cta?.map((cta) => transformCta(cta)),
        video: video && { src: video.fields.file.url, title: video.fields.title },
        youtubeVideo: cleanContentfulId(card.fields.youtubeVideo),
    };
};

export const transformBlockContent = (blockContent: BlockContentType): BlockContentData => {
    if (!blockContent) {
        return undefined;
    }

    const backgroundColor = match<BlockContentType['fields']['colorOption'], BlockContentBackground>(
        cleanContentfulId(blockContent.fields.colorOption),
    )
        .with('color-dark-purple', () => 'dark')
        .otherwise(() => undefined);

    const isHeaderNegative = backgroundColor === 'dark';

    return {
        id: blockContent.sys.id,
        backgroundColor,
        isHeaderNegative,
        blockTitle: blockContent.fields.title,
        blockCta: transformCta(blockContent.fields.cta),
        cards: blockContent.fields.cards?.map((card, index) => transformContentCard(card, backgroundColor, index)),
    };
};
